import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Check, FileText, Search } from 'lucide-react'
import { useState } from 'react'
import { get } from '../lib/api.ts'
import { fmtDate } from '../lib/format.ts'
import { useCondo } from '../lib/hooks.ts'
import type { DocRow } from '../lib/types.ts'
import { Badge, Button, Input, Sheet } from '../components/ui.tsx'
import { HomeExtras } from './home-extras.tsx'
import { t } from '../lib/i18n.ts'

export function CondoHome() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId, info, me, can } = useCondo(slug)
  const nav = useNavigate()
  const [q, setQ] = useState('')
  const docs = useQuery({ queryKey: ['docs', condoId, ''], queryFn: () => get<{ documents: DocRow[] }>(`/api/condos/${condoId}/documents`), enabled: Boolean(condoId) })
  const isManager = can('documents.upload')
  const units = useQuery({ queryKey: ['units', condoId], queryFn: () => get<{ units: { id: string }[] }>(`/api/condos/${condoId}/units`), enabled: Boolean(condoId && isManager) })
  const members = useQuery({ queryKey: ['members', condoId], queryFn: () => get<{ members: { id: string }[] }>(`/api/condos/${condoId}/members`), enabled: Boolean(condoId && can('members.view')) })
  const firstName = me.data?.user?.name.split(' ')[0] ?? ''
  const list = docs.data?.documents ?? []
  const has = (cat: string) => list.some((d) => d.category === cat)
  const steps = [
    { done: has('convencao'), label: t('Enviar a convenção do condomínio'), to: 'acervo' },
    { done: has('regimento'), label: t('Enviar o regimento interno'), to: 'acervo' },
    { done: has('ata'), label: t('Enviar as atas das últimas assembleias'), to: 'acervo' },
    { done: (units.data?.units.length ?? 0) > 0, label: t('Cadastrar as unidades'), to: 'unidades' },
    { done: (members.data?.members.length ?? 0) > 1, label: t('Convidar moradores e conselho'), to: 'membros' },
  ]
  return (
    <div>
      <h1 className="text-[1.7rem] font-bold leading-tight sm:text-[2rem]">{t('Olá,')}{' '}{firstName}</h1>
      <p className="mt-1 hidden text-tinta-2 md:block">{info.data?.condo.name}</p>

      <form
        className="almaco mt-6 rounded-ficha border border-pauta py-5 pr-4 pl-[3.5rem] sm:pr-6"
        onSubmit={(e) => {
          e.preventDefault()
          if (q.trim()) nav({ to: '/c/$slug/assistente', params: { slug }, search: { q } as never })
        }}
      >
        <label htmlFor="ask" className="block font-doc text-[1.25rem] font-semibold">
          {t('O que você quer saber sobre o condomínio?')}
        </label>
        <div className="mt-3 flex gap-2">
          <Input id="ask" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Ex.: até que horas pode ter barulho?')} className="bg-folha/90" />
          <Button type="submit" aria-label={t('Perguntar')}><Search className="size-5" /><span className="hidden sm:inline">{t('Perguntar')}</span></Button>
        </div>
        <p className="mt-2 text-sm text-tinta-2">{t('As respostas vêm dos documentos do acervo, com a fonte de cada informação.')}</p>
      </form>

      {isManager && steps.some((s) => !s.done) && (
        <Sheet className="mt-6">
          <h2 className="border-b border-pauta px-4 py-3 font-bold">{t('Primeiros passos')}</h2>
          <ol>
            {steps.map((s) => (
              <li key={s.label} className="flex items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
                <span className={s.done ? 'grid size-6 place-items-center rounded-full bg-carimbo text-white' : 'grid size-6 place-items-center rounded-full border-2 border-pauta'}>
                  {s.done && <Check className="size-4" />}
                </span>
                <Link to={`/c/${slug}/${s.to}`} className={s.done ? 'text-tinta-2 line-through' : 'font-semibold hover:text-caneta'}>
                  {s.label}
                </Link>
              </li>
            ))}
          </ol>
        </Sheet>
      )}

      <HomeExtras slug={slug} condoId={condoId} />

      <section className="mt-8">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-xl font-bold">{t('Documentos recentes')}</h2>
          <Link to="/c/$slug/acervo" params={{ slug }} className="font-semibold text-caneta">{t('Ver acervo')}</Link>
        </div>
        {list.length === 0 ? (
          <p className="text-tinta-2">{t('Nenhum documento ainda.')}</p>
        ) : (
          <Sheet>
            {list.slice(0, 5).map((d) => (
              <Link key={d.id} to="/c/$slug/acervo/$docId" params={{ slug, docId: d.id }} className="flex items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0 hover:bg-papel">
                <FileText className="size-5 shrink-0 text-tinta-3" />
                <span className="min-w-0 flex-1 truncate font-semibold">{d.title}</span>
                <span className="hidden text-sm text-tinta-2 sm:inline">{t(d.categoryLabel)}</span>
                <span className="text-sm text-tinta-2">{fmtDate(d.docDate ?? d.createdAt)}</span>
              </Link>
            ))}
          </Sheet>
        )}
      </section>
      {info.data && !info.data.condo.externalAiProcessing && <Badge className="mt-6" tone="alerta">{t('Processamento externo por IA desligado')}</Badge>}
    </div>
  )
}
